/*
 * bloggle-legacy.js — Zorali in-house runtime for articles built with the Bloggle editor.
 *
 * Written from scratch for Zorali (Oct 2026) to replace the behaviour of Bloggle's hosted
 * article script, so legacy article markup renders with no requests to Bloggle's servers.
 * It only reads the markup Bloggle left in body_html; it never changes article data.
 *
 *   1. Product cards  (.bggle_product-container[bggle-product-handle]) are filled from
 *      /products/<handle>.json: title, price, compare-at price, image. Cards for missing
 *      products are removed. When every card in a .bggle_product-slider has settled, the
 *      slider's `init-slider-<section-id>` event fires (the inline config in the body
 *      listens for it and starts tiny-slider).
 *   2. Currency symbol spans are set from the active storefront currency.
 *   3. Image sliders (.bggle--slider-image) get their `init-slider-<id>` event.
 *   4. Table-of-contents links (.bggle--anchor-link) scroll smoothly to their section.
 *   5. Lazy video iframes: data-src -> src.
 *   6. Articles that never linked the legacy stylesheet get it appended to <head>.
 */
(function () {
  'use strict';

  var SYMBOLS = {
    AUD: '$', USD: '$', NZD: '$', CAD: '$', SGD: '$', HKD: '$', MXN: '$', CLP: '$',
    GBP: '£', EUR: '€', JPY: '¥', CNY: '¥', KRW: '₩', INR: '₹', IDR: 'Rp', PHP: '₱',
    THB: '฿', VND: '₫', MYR: 'RM', TWD: 'NT$', CHF: 'CHF', SEK: 'kr', NOK: 'kr',
    DKK: 'kr', ISK: 'kr', PLN: 'zł', CZK: 'Kč', HUF: 'Ft', RON: 'lei', BGN: 'лв',
    AED: 'AED', ILS: '₪', ZAR: 'R', BRL: 'R$', TRY: '₺'
  };

  function currencyCode() {
    return (window.Shopify && Shopify.currency && Shopify.currency.active) || null;
  }

  function symbolFor(code) {
    if (!code) return '$';
    if (SYMBOLS[code]) return SYMBOLS[code];
    try {
      var parts = new Intl.NumberFormat('en', { style: 'currency', currency: code, currencyDisplay: 'narrowSymbol' }).formatToParts(0);
      for (var i = 0; i < parts.length; i++) if (parts[i].type === 'currency') return parts[i].value;
    } catch (e) {}
    return code;
  }

  // Storefront root, including any market/locale prefix such as "/en-us/".
  function root() {
    if (window.Shopify && Shopify.routes && Shopify.routes.root) return Shopify.routes.root;
    var seg = window.location.pathname.split('/')[1] || '';
    return (seg !== 'blogs' && seg !== 'pages' && /^[a-z]{2}(-[a-z]{2})?$/i.test(seg)) ? '/' + seg + '/' : '/';
  }

  // Price text as the cards have always shown it: the raw decimal string, with
  // thousands separators above 999.99, and the market-specific tweaks for zł / Ft / Kč.
  function priceText(raw, symbol) {
    var value = parseFloat(raw);
    var text = String(raw);
    if (value > 999.99) text = text.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    if ((symbol === 'Ft' || symbol === 'Kč') && /\.0+$/.test(text)) text = text.split('.')[0];
    if (/zł/.test(symbol)) text = text.replace('.', ',');
    return text;
  }

  // "…/file.jpg?v=1" -> "…/file_800x.jpg?v=1"
  function sizedImage(src, width) {
    return src.replace(/\.([^.\/]+)$/, '_' + width + 'x.$1');
  }

  var productRequests = {};

  function loadProduct(handle) {
    if (productRequests[handle]) return productRequests[handle];
    var url = root() + 'products/' + encodeURIComponent(handle);
    productRequests[handle] = fetch(url + '.json', { credentials: 'same-origin' })
      .then(function (res) {
        if (res.ok) return res.json();
        if (res.status !== 404) throw new Error('product ' + res.status);
        // Renamed product: follow the storefront redirect, then read the new handle.
        return fetch(url, { method: 'HEAD', credentials: 'same-origin' }).then(function (head) {
          if (!head.ok || !/\/products\//.test(head.url)) throw new Error('product gone');
          var target = head.url.split('?')[0].split('#')[0];
          return fetch(target + '.json', { credentials: 'same-origin' }).then(function (r) {
            if (!r.ok) throw new Error('product ' + r.status);
            return r.json();
          });
        });
      })
      .then(function (data) {
        if (!data || !data.product) throw new Error('no product');
        return data.product;
      });
    return productRequests[handle];
  }

  function fillCard(card, product, imageWidth, symbol) {
    var variant = product.variants && product.variants[0];
    var title = card.querySelector('.bloggle_product--title');
    if (title) title.textContent = product.title;
    var vendor = card.querySelector('.bloggle--vendor');
    if (vendor) vendor.textContent = product.vendor;
    if (variant) {
      var price = card.querySelector('.bloggle_product--price');
      if (price) price.textContent = priceText(variant.price, symbol);
      var compare = parseFloat(variant.compare_at_price);
      var compareEl = card.querySelector('.bloggle_product--compare-at-price');
      if (compare && compare > parseFloat(variant.price)) {
        if (compareEl) compareEl.textContent = priceText(variant.compare_at_price, symbol);
      } else {
        var wrap = card.querySelector('.bggle--compare');
        if (wrap) wrap.style.display = 'none';
      }
    }
    if (product.image && product.image.src) {
      var img = card.querySelector('.bloggle_product--img');
      if (img) {
        img.loading = 'lazy';
        img.src = sizedImage(product.image.src, imageWidth);
        img.setAttribute('alt', product.image.alt || product.title);
      }
    }
  }

  function fire(id) {
    document.dispatchEvent(new CustomEvent('init-slider-' + id));
  }

  function setupProducts(symbol) {
    var article = document.getElementById('bloggy--article');
    var width = Math.max((article && article.offsetWidth) || 750, 420);

    function run(card) {
      var handle = card.getAttribute('bggle-product-handle');
      if (!handle) { card.remove(); return Promise.resolve(); }
      return loadProduct(handle).then(
        function (product) { fillCard(card, product, width, symbol); },
        function () { card.remove(); }
      );
    }

    document.querySelectorAll('.bggle_product-slider').forEach(function (slider) {
      var cards = slider.querySelectorAll('.bggle_product-container:not(.custom-product)');
      var jobs = Array.prototype.map.call(cards, run);
      Promise.all(jobs).then(function () { fire(slider.getAttribute('section-id')); });
    });

    document.querySelectorAll(
      '.bggle_product-with-text .bggle_product-container:not(.custom-product),' +
      '.bggle_one-product .bggle_product-container:not(.custom-product),' +
      '.bggle_product-grid .bggle_product-container:not(.custom-product)'
    ).forEach(run);
  }

  function easeInOutQuad(t, start, delta, duration) {
    t /= duration / 2;
    if (t < 1) return (delta / 2) * t * t + start;
    t--;
    return (-delta / 2) * (t * (t - 2) - 1) + start;
  }

  function setupAnchors() {
    document.querySelectorAll('.bggle--anchor-link').forEach(function (link) {
      link.addEventListener('click', function (event) {
        event.preventDefault();
        var id = (link.getAttribute('href') || '').replace('#', '');
        var target = document.querySelector('.bggle--block[section-id="' + id + '"]') || document.getElementById(id);
        if (!target) return;
        var offset = link.getAttribute('move-scroll') || 0;
        if (window.screen.width < 768) offset = link.getAttribute('move-scroll-mobile') || link.getAttribute('move-scroll') || 0;
        offset = Number(offset) || 0;
        var from = window.scrollY;
        var began = null;
        var duration = 1000;
        requestAnimationFrame(function step(now) {
          if (began === null) began = now;
          var elapsed = now - began;
          var to = target.getBoundingClientRect().top + window.scrollY - offset;
          window.scrollTo(0, elapsed < duration ? easeInOutQuad(elapsed, from, to - from, duration) : to);
          if (elapsed < duration) requestAnimationFrame(step);
        });
      });
    });
  }

  function ensureStylesheet() {
    var href = window.zoraliBloggleLegacyCss;
    if (!href) return;
    var file = href.split('?')[0];
    var links = document.querySelectorAll('link[rel="stylesheet"]');
    for (var i = 0; i < links.length; i++) if (links[i].href.split('?')[0].indexOf(file.replace(/^https?:/, '')) !== -1) return;
    var link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = href;
    document.head.appendChild(link);
  }

  function init() {
    var symbol = symbolFor(currencyCode());
    document.querySelectorAll('.bloggle_product--price-currency').forEach(function (el) { el.textContent = symbol; });

    setupAnchors();

    document.querySelectorAll('.bggle--slider-blog').forEach(function (el) {
      var id = el.getAttribute('section-id');
      setTimeout(function () { fire(id); }, 100);
    });
    document.querySelectorAll('.bggle--slider-image').forEach(function (el) { fire(el.getAttribute('section-id')); });

    setupProducts(symbol);
    ensureStylesheet();

    document.querySelectorAll('.bggle--youtube-video, .bggle--vimeo-video, .bggle--html-video source').forEach(function (el) {
      var src = el.getAttribute('data-src');
      if (src) { el.setAttribute('src', src); el.removeAttribute('data-src'); }
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();
})();
